import React, { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormState } from 'react-final-form';
import { FilterLocations } from '../../common/FilterLocations';
import {
  filterButtonStyles,
  FilterWrapper,
  SearchBarWrapper,
  SearchPageFiltersContainer,
  GoBackButton
} from './styled';
import { BuildingTypeFilter } from '../BuildingTypeFilter';
import useScreenSize from '../../../../hooks/useScreenSize';
import { LeasedFilter } from '../LeasedFilter';
import { Button } from '../../../ui/Button';
import { ButtonType } from '../../../../styled/theme/types';
import Icon from '../../../Icon';
import FilterIcon from '../../../../public/static/images/new_search/icons/icon-filter.svg';
import { RoomsFilter } from '../RoomsFilter';
import { FLAT_CATEGORIES } from '../../../../utils/helpers/constants';
import { AreaFilter } from '../AreaFilter';
import { PriceFilter } from '../PriceFilter';
import { FilterFieldProps, ModalParams, ModalTypeEnum } from '../types';
import { useClient } from '../../../../hooks/useClient';
import { Category } from '../../../../graphql/types';

type SearchPageFiltersProps = {
  shortMode?: boolean;
  canGoBack?: boolean;
  isShowHomeFilters?: boolean;
  onGoBack?: () => void;
  setModalParams?: React.Dispatch<React.SetStateAction<ModalParams>>;
  isPashaLanding?: boolean;
  additionalButtons?: unknown;
  categories?: Category[];
};

type FiltersConfig = {
  component: FunctionComponent<FilterFieldProps>;
  order: number;
  condition?: boolean;
};

export const SearchPageFilters = ({
  setModalParams,
  shortMode,
  canGoBack,
  isShowHomeFilters,
  onGoBack,
  isPashaLanding,
  categories,
  additionalButtons = null
}: SearchPageFiltersProps) => {
  const { t } = useTranslation();
  const isClient = useClient();
  const { isSmall, isLarge, isMedium } = useScreenSize();
  const { values } = useFormState();
  const isFlatCategory = FLAT_CATEGORIES.includes(values.category_id);

  const openFilters = () =>
    setModalParams &&
    setModalParams({
      initialScreen: ModalTypeEnum.Filters
    });

  const filtersConfig: FiltersConfig[] = [
    {
      component: LeasedFilter,
      order: 1
    },
    {
      component: BuildingTypeFilter,
      order: 2
    },
    {
      component: PriceFilter,
      order: isSmall && isClient ? 3 : 4
    },
    {
      component: isFlatCategory ? RoomsFilter : AreaFilter,
      condition: isShowHomeFilters || isPashaLanding ? true : !isMedium,
      order: isSmall && isClient ? 4 : 3
    }
  ];

  const sortedFiltersConfig = filtersConfig
    .filter(({ condition = true }) => condition)
    .sort((a, b) => a.order - b.order);

  return (
    <SearchPageFiltersContainer
      $canGoBack={canGoBack}
      $isShowHomeFilters={isShowHomeFilters}
    >
      {canGoBack && (
        <GoBackButton data-cy="go-back-button" onClick={onGoBack} />
      )}

      {!shortMode && (
        <>
          {sortedFiltersConfig.map(({ component: FilterComponent, order }) => (
            <FilterWrapper
              key={order.toString()}
              $isPashaLanding={isPashaLanding}
            >
              <FilterComponent
                submitOnChange
                isPashaLanding={isPashaLanding}
                categories={categories}
              />
            </FilterWrapper>
          ))}
        </>
      )}

      {!isPashaLanding && (
        <>
          <SearchBarWrapper
            canGoBack={canGoBack && shortMode}
            $isShowHomeFilters={isShowHomeFilters}
          >
            <FilterLocations setModalParams={setModalParams} />
          </SearchBarWrapper>
          <Button
            onClick={openFilters}
            onKeyPress={openFilters}
            cypress="new-search-filters-button"
            stat="new-search-filters-button"
            styles={filterButtonStyles(isShowHomeFilters ?? false)}
            state={ButtonType.white}
          >
            <Icon
              width={20}
              height={20}
              indent={false}
              IconComponent={FilterIcon}
            />
            {isLarge && t('search.buttons.filters')}
          </Button>
        </>
      )}
      {additionalButtons}
    </SearchPageFiltersContainer>
  );
};
